import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const empty = {
  legal_entity_name: "", jurisdiction: "", registration_number: "",
  beneficial_owners_text: "", is_pep: false, pep_details: "",
  countries_text: "",
  attest_no_sanctioned_exposure: false,
  attest_source_of_funds: false,
  attest_beneficial_ownership_accuracy: false,
};

export default function Compliance() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const c = user?.compliance;
    if (c) {
      setForm({
        legal_entity_name: c.legal_entity_name || "",
        jurisdiction: c.jurisdiction || "",
        registration_number: c.registration_number || "",
        beneficial_owners_text: (c.beneficial_owners || []).join(", "),
        is_pep: !!c.is_pep,
        pep_details: c.pep_details || "",
        countries_text: (c.countries_of_operation || []).join(", "),
        attest_no_sanctioned_exposure: !!c.attest_no_sanctioned_exposure,
        attest_source_of_funds: !!c.attest_source_of_funds,
        attest_beneficial_ownership_accuracy: !!c.attest_beneficial_ownership_accuracy,
      });
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.patch("/auth/me/compliance", {
        legal_entity_name: form.legal_entity_name.trim(),
        jurisdiction: form.jurisdiction.trim(),
        registration_number: form.registration_number.trim(),
        beneficial_owners: form.beneficial_owners_text.split(",").map((s) => s.trim()).filter(Boolean),
        is_pep: form.is_pep,
        pep_details: form.pep_details,
        countries_of_operation: form.countries_text.split(",").map((s) => s.trim()).filter(Boolean),
        attest_no_sanctioned_exposure: form.attest_no_sanctioned_exposure,
        attest_source_of_funds: form.attest_source_of_funds,
        attest_beneficial_ownership_accuracy: form.attest_beneficial_ownership_accuracy,
      });
      await refresh();
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  const status = user?.compliance?.kyc_status;
  const allAttest = form.attest_no_sanctioned_exposure && form.attest_source_of_funds && form.attest_beneficial_ownership_accuracy;

  return (
    <DashboardLayout testid="compliance-page">
      <div className="p-8 md:p-12 max-w-3xl">
        <div className="overline">KYC · AML · Sanctions screening</div>
        <h1 className="h-serif text-5xl text-ink mt-3">Compliance profile</h1>
        <p className="text-sub mt-4 leading-relaxed">
          Under U.S. federal law, every counterparty introduced through this brokerage must
          clear OFAC sanctions screening and basic AML diligence before a deal can be initiated.
          Complete this profile once. Our compliance desk will review and clear within 24 business hours.
        </p>

        {status && (
          <div className="mt-6 border-l-4 border-oxblood bg-parchment2 px-4 py-3" data-testid="kyc-status">
            <div className="overline">Current status</div>
            <div className="font-mono text-sm text-ink mt-1 uppercase tracking-wider">{status}</div>
            {user?.compliance?.reviewed_by && (
              <div className="text-xs text-sub mt-1 font-mono">
                Reviewed by {user.compliance.reviewed_by} · {user.compliance.reviewed_at?.slice(0, 10)}
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="mt-10 space-y-7" data-testid="kyc-form">
          <Section title="Corporate identity">
            <F label="Legal entity name" req>
              <input className="input-line" required value={form.legal_entity_name} onChange={set("legal_entity_name")} data-testid="kyc-entity" />
            </F>
            <F label="Jurisdiction of incorporation" req>
              <input className="input-line" required value={form.jurisdiction} onChange={set("jurisdiction")} placeholder="Delaware, USA" data-testid="kyc-jurisdiction" />
            </F>
            <F label="Corporate registration number" req>
              <input className="input-line" required value={form.registration_number} onChange={set("registration_number")} data-testid="kyc-regnum" />
            </F>
            <F label="Beneficial owners — comma-separated full names" req>
              <input className="input-line" required value={form.beneficial_owners_text} onChange={set("beneficial_owners_text")} placeholder="Jane Smith, John Doe" data-testid="kyc-owners" />
            </F>
            <F label="Countries of operation — comma-separated" req>
              <input className="input-line" required value={form.countries_text} onChange={set("countries_text")} placeholder="United States, Germany, UAE" data-testid="kyc-countries" />
            </F>
          </Section>

          <Section title="Politically Exposed Person (PEP) status">
            <label className="flex gap-3 items-start cursor-pointer">
              <input type="checkbox" checked={form.is_pep} onChange={set("is_pep")} data-testid="kyc-is-pep" className="mt-1" />
              <span className="text-sub text-sm leading-relaxed">
                One or more beneficial owners is a Politically Exposed Person (current or former senior public official, family member of one, or close associate).
              </span>
            </label>
            {form.is_pep && (
              <F label="PEP details">
                <textarea rows={2} className="input-line resize-none" value={form.pep_details} onChange={set("pep_details")} data-testid="kyc-pep-details" />
              </F>
            )}
          </Section>

          <Section title="Attestations — all three required">
            <Attest k="attest_no_sanctioned_exposure" form={form} set={set} testid="attest-sanctions">
              I attest that neither the legal entity, its beneficial owners, nor its affiliated parties are subject to U.S. OFAC, UN, EU or UK sanctions, and have no operations in any sanctioned jurisdiction.
            </Attest>
            <Attest k="attest_source_of_funds" form={form} set={set} testid="attest-funds">
              I attest that all funds used in transactions through this brokerage derive from lawful commercial activity, and I will provide bank-issued proof of funds (BCL) on request for each deal.
            </Attest>
            <Attest k="attest_beneficial_ownership_accuracy" form={form} set={set} testid="attest-ownership">
              I attest that the beneficial ownership information above is accurate and complete to the best of my knowledge, and I will notify the brokerage within 14 days of any material change.
            </Attest>
          </Section>

          {error && <div className="border-l-4 border-danger bg-parchment p-4 text-danger" data-testid="kyc-error">{error}</div>}

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-sub">False attestations may constitute federal wire fraud.</p>
            <button type="submit" className="btn-primary" disabled={busy || !allAttest} data-testid="kyc-submit">
              {busy ? "Submitting…" : status === "cleared" ? "Update profile →" : "Submit for review →"}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-line pt-6">
      <div className="overline">{title}</div>
      <div className="mt-4 space-y-5">{children}</div>
    </div>
  );
}
function F({ label, children, req }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}{req && <span className="text-oxblood ml-1">*</span>}</span>
      {children}
    </label>
  );
}
function Attest({ k, form, set, testid, children }) {
  return (
    <label className="flex gap-3 items-start cursor-pointer">
      <input type="checkbox" checked={form[k]} onChange={set(k)} className="mt-1" data-testid={testid} />
      <span className="text-sub text-sm leading-relaxed">{children}</span>
    </label>
  );
}
